import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ROUTES } from '@constants/index'
import { activeClaimPerformedSelector } from '@redux/slices/claimStatusSlice'
import { connectorSessionIdSelector, isAnonymousUserSelector, isGuestUserSelector } from '@redux/slices/connectorSlice'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInvokeAction } from '@hooks/useInvokeAction'
import { useInsuranceUsage } from '@hooks/useInsuranceUsage'
import { drawerEventEmitter } from '@utils/drawerEventEmitter'
import { getInsuranceToggleState, getInsuranceToggleStateForCart } from '@utils/insuranceToggleUtil'

type ToggleState = {
  toggleOn?: boolean
  allowOn?: boolean
  allowSelectProduct?: boolean
}

type UseInsuranceToggleReturn = {
  toggleState: ToggleState
  isLoading: boolean
  isUpdatingToggle: boolean
  onShowBenefits: () => void
  onToggleClick: (e: React.MouseEvent) => void
  onToggleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectProduct: () => void
}

export const useInsuranceToggle = (): UseInsuranceToggleReturn => {
  const { cartPath } = useEnvironment()
  const [isLoading, setLoading] = useState(true)
  const invokeAction = useInvokeAction()
  const isAnonymousUser = useSelector(isAnonymousUserSelector)
  const isGuestUser = useSelector(isGuestUserSelector)
  const sessionId = useSelector(connectorSessionIdSelector)
  const activeCheckPerformed = useSelector(activeClaimPerformedSelector)
  const { claimRecord, claimRecordDeleted, isLoading: isLoadingClaimRecord } = useClaimRecord()
  const { enableInsuranceUsage, disableInsuranceUsage, isLoading: isUpdatingToggle } = useInsuranceUsage()

  useEffect(() => {
    if (isAnonymousUser || (isGuestUser && !sessionId) || (activeCheckPerformed && !isLoadingClaimRecord)) {
      setLoading(false)
    }
  }, [isAnonymousUser, isGuestUser, sessionId, activeCheckPerformed, isLoadingClaimRecord])

  const toggleState = useMemo<ToggleState>(() => {
    // eslint-disable-next-line no-restricted-globals
    const isCart = window.location.pathname.includes(cartPath)

    return isCart
      ? getInsuranceToggleStateForCart({ claimRecord, claimRecordDeleted })
      : getInsuranceToggleState({ claimRecord, claimRecordDeleted })
  }, [claimRecord, claimRecordDeleted])

  const onToggleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    invokeAction(() => {
      const { toggleOn, allowOn } = toggleState
      if (toggleOn) {
        disableInsuranceUsage()
      } else {
        allowOn && enableInsuranceUsage()
        !allowOn && drawerEventEmitter.openDrawer({ target: ROUTES.INSURANCE_MEMBER })
      }
    })
  }

  const onShowBenefits = () => {
    invokeAction(() => {
      drawerEventEmitter.openDrawer({
        target: ROUTES.INSURANCE_MEMBER,
      })
    })
  }

  const onToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // to bypass react warning; no use at this time
  }

  const onSelectProduct = () => {
    invokeAction(() => {
      drawerEventEmitter.openDrawer({ target: ROUTES.CHANGE_COVERED_PRODUCT })
    })
  }

  return {
    isLoading,
    isUpdatingToggle,
    toggleState,
    onToggleClick,
    onShowBenefits,
    onToggleChange,
    onSelectProduct,
  }
}
